import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChatBubbleLeftRightIcon, XMarkIcon, CheckIcon, SparklesIcon } from './Icons';
import Spinner from './Spinner';
import { submitFeedback } from '../../services/apiService';
import { FAB_BOTTOM_FEEDBACK, FAB_RIGHT, FAB_Z_OVERLAY, fabPortalFixedStyle } from '../../constants/fabDock';

const FEATURES_CONFIG = [
    { id: 'LessonPlanCreator', label: 'Tạo giáo án' },
    { id: 'QuizCreator', label: 'Thiết kế câu hỏi' },
    { id: 'GameCreator', label: 'Trò chơi giáo dục' },
    { id: 'SlideCreator', label: 'Trình chiếu (Slides)' },
    { id: 'HomeworkCreator', label: 'Tạo bài tập / ôn tập' },
    { id: 'ActivityCreator', label: 'Hoạt động sáng tạo' },
    { id: 'VideoCreator', label: 'Tạo video' },
    { id: 'ScienceExplainer', label: 'Giải thích khoa học' },
    { id: 'HistoryStoryteller', label: 'Kể chuyện lịch sử' },
    { id: 'LiteratureAssistant', label: 'Hỗ trợ văn học' },
    { id: 'Drawing', label: 'Vẽ tranh AI' },
];

const StarRating: React.FC<{ rating: number; setRating: (rating: number) => void; size?: 'sm' | 'md' }> = ({ rating, setRating, size = 'md' }) => {
    const iconSize = size === 'sm' ? 'w-5 h-5' : 'w-8 h-8';
    return (
        <div className="flex justify-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="focus:outline-none"
                    aria-label={`Rate ${star} stars`}
                >
                    <svg
                        className={`${iconSize} transition-colors ${
                            star <= rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                </button>
            ))}
        </div>
    );
};

const Feedback: React.FC<{ currentView?: string }> = ({ currentView }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [viewMode, setViewMode] = useState<'current' | 'multi'>('current');
    const [generalRating, setGeneralRating] = useState(0);
    const [multiRatings, setMultiRatings] = useState<Record<string, number>>({});
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const ratingsToSend: { feature: string; rating: number }[] = [];

        if (viewMode === 'current') {
            if (generalRating > 0 || comment.trim()) {
                ratingsToSend.push({ feature: currentView || 'general', rating: generalRating });
            }
        } else {
            Object.entries(multiRatings).forEach(([feature, rating]) => {
                if (rating > 0) ratingsToSend.push({ feature, rating });
            });
        }

        if (ratingsToSend.length === 0 && !comment.trim()) return;

        const now = Date.now();
        if (now - lastSubmitTime < 30000) {
            setError('Vui lòng đợi 30 giây trước khi gửi góp ý tiếp theo.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            let successCount = 0;
            let lastMessage = '';

            for (const item of ratingsToSend) {
                try {
                    const result = await submitFeedback(item.rating, item.feature === currentView ? comment : '', item.feature);
                    if (result.success) successCount++;
                } catch (err: any) {
                    lastMessage = err.message;
                }
            }

            if (ratingsToSend.length === 0 && comment.trim()) {
                const result = await submitFeedback(0, comment, currentView || 'general');
                if (result.success) successCount++;
                else lastMessage = result.message;
            }

            if (successCount > 0) {
                setLastSubmitTime(now);
                setIsSubmitted(true);
                setTimeout(() => {
                    setIsSubmitted(false);
                    setIsExpanded(false);
                    setGeneralRating(0);
                    setMultiRatings({});
                    setComment('');
                    setViewMode('current');
                }, 3000);
            } else {
                setError(lastMessage || 'Không thể gửi góp ý. Có thể bạn đã gửi cho các chức năng này hôm nay.');
            }
        } catch (err: any) {
            setError(err.message || 'Không thể kết nối đến máy chủ.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const collapsedFab = (
        <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsExpanded(true); } }}
            onClick={() => setIsExpanded(true)}
            className="min-w-14 h-14 px-2 bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:bg-teal-50 group transition-all duration-300 hover:scale-110 active:scale-95 touch-none overflow-visible"
            style={fabPortalFixedStyle(FAB_BOTTOM_FEEDBACK)}
            title="Gửi góp ý"
        >
            <ChatBubbleLeftRightIcon className="h-7 w-7 text-teal-600 group-hover:rotate-12 transition-transform shrink-0" />
            <span className="max-w-0 overflow-hidden opacity-0 group-hover:max-w-[8rem] group-hover:opacity-100 transition-all duration-300 whitespace-nowrap font-black uppercase tracking-widest text-[8px] text-teal-800 leading-none mt-1">
                Góp ý
            </span>
        </div>
    );

    const expandedPanel = (
        <div
            className="w-[min(24rem,calc(100vw-2rem))] max-h-[min(80vh,calc(100vh-6rem))] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
            style={{
                position: 'fixed',
                zIndex: FAB_Z_OVERLAY,
                right: FAB_RIGHT,
                top: 'max(5rem, env(safe-area-inset-top, 0px))',
                left: 'auto',
                bottom: 'auto',
            }}
        >
            <div className="p-4 flex items-center justify-between border-b bg-slate-50/50">
                <div className="flex items-center gap-2 min-w-0">
                    <SparklesIcon className="h-5 w-5 text-teal-500 shrink-0" />
                    <h3 className="text-sm font-bold text-gray-800 truncate">Góp ý trải nghiệm</h3>
                </div>
                <button type="button" onClick={() => setIsExpanded(false)} className="p-1.5 rounded-full hover:bg-gray-200 transition-colors shrink-0" title="Đóng">
                    <XMarkIcon className="h-4 w-4 text-gray-600" />
                </button>
            </div>

            <div className="overflow-y-auto custom-scrollbar flex-grow min-h-0">
                <div className="p-5">
                    {isSubmitted ? (
                        <div className="text-center py-10">
                            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <CheckIcon className="h-10 w-10 text-green-600 animate-bounce"/>
                            </div>
                            <p className="text-lg font-bold text-gray-800">Tuyệt vời!</p>
                            <p className="text-sm text-gray-500 mt-1">Cảm ơn bạn đã dành thời gian góp ý giúp Edu App ngày càng hoàn thiện hơn.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-xs text-rose-600 animate-pulse">
                                    {error}
                                </div>
                            )}

                            <div className="flex p-1 bg-slate-100 rounded-xl">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('current')}
                                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${viewMode === 'current' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Tính năng hiện tại
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('multi')}
                                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${viewMode === 'multi' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Đánh giá nhiều mục
                                </button>
                            </div>

                            {viewMode === 'current' ? (
                                <div className="space-y-4">
                                    <div className="text-center space-y-2 bg-teal-50/50 p-4 rounded-2xl border border-teal-100/50">
                                        <p className="text-xs font-bold text-teal-800 uppercase tracking-wider">
                                            {FEATURES_CONFIG.find(f => f.id === currentView)?.label || 'Trải nghiệm chung'}
                                        </p>
                                        <StarRating rating={generalRating} setRating={setGeneralRating} />
                                    </div>
                                    <textarea
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        rows={4}
                                        placeholder="Hãy chia sẻ thêm cảm nhận hoặc ý tưởng của bạn..."
                                        className="w-full p-3 border border-gray-200 rounded-2xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all outline-none resize-none"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Chọn chức năng bạn muốn đánh giá</p>
                                    <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                        {FEATURES_CONFIG.map(feature => (
                                            <div key={feature.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white hover:border-teal-200 transition-colors">
                                                <span className="text-xs font-medium text-gray-700">{feature.label}</span>
                                                <StarRating
                                                    size="sm"
                                                    rating={multiRatings[feature.id] || 0}
                                                    setRating={(r) => setMultiRatings(prev => ({ ...prev, [feature.id]: r }))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-2xl shadow-lg shadow-teal-100 text-sm font-black text-white bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 transition-all active:scale-95"
                            >
                                {isSubmitting ? <Spinner size="sm" /> : 'GỬI ĐÁNH GIÁ'}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            <div className="p-3 text-center bg-slate-50 border-t shrink-0">
                <p className="text-[10px] text-gray-400 italic">Edu App trân trọng mọi ý kiến đóng góp từ bạn!</p>
            </div>
        </div>
    );

    if (!mounted || typeof document === 'undefined') return null;
    if (!isExpanded) return createPortal(collapsedFab, document.body);
    return createPortal(expandedPanel, document.body);
};

export default Feedback;
